// Implements: F-UIX-009, F-UIX-010, F-UIX-013
/**
 * The session list: the container that owns the query, the confirmation and
 * the notice.
 *
 * The three states are components and not inline `if`s (F-UIX-010):
 * `LoadingState` while the query is pending, `EmptyState` for a successful
 * answer with nothing in it, `ErrorState` with a retry when it failed. No
 * state is spelled out as loose text in the middle of the markup, which is the
 * habit the capability exists to remove.
 *
 * Revocation always asks first, with `ConfirmDialog` and never `confirm()` —
 * ESLint forbids the native one (F-UIX-013). The question is asked about a
 * specific row, so the pending row lives in state: a dialog that confirms "the
 * last button you pressed" is a dialog that revokes the wrong device the day
 * two of them are on screen. Since mockup v3 P1 the current device's row has no
 * button (it is closed from the user menu), so every question is about another
 * device.
 *
 * The list is the "Sesiones activas" section of `/cuenta` (mockup v3 P1). The
 * page may put its own `toolbar` above the table and `footer` below it, both
 * drawn from the same answer, so a count or a "close the others" action never
 * disagrees with the rows on screen.
 */

import { useCallback, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { SessionSummary } from "../../../types/api";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { SessionRow } from "../components/SessionRow";
import { useRevokeSession } from "../hooks/useRevokeSession";
import { useSessions } from "../hooks/useSessions";
import styles from "./SessionListContainer.module.css";

export interface SessionListContainerProps {
  /** Drawn above the table from the listed sessions (mockup v3 P1: the count and "close the others"). */
  readonly toolbar?: ((sessions: readonly SessionSummary[]) => ReactNode) | undefined;
  /** Drawn below the table from the listed sessions. */
  readonly footer?: ((sessions: readonly SessionSummary[]) => ReactNode) | undefined;
}

export function SessionListContainer({ toolbar, footer }: SessionListContainerProps = {}): ReactElement {
  const { t } = useTranslation();
  const sessions = useSessions();
  const { revoke, pending } = useRevokeSession();
  const { push } = useToast();
  const [asking, setAsking] = useState<SessionSummary | null>(null);

  const confirm = useCallback(async (): Promise<void> => {
    if (asking === null) return;
    const target = asking;
    setAsking(null);
    const revoked = await revoke(target);
    if (!revoked) {
      // A refusal or a dead network leaves the row exactly where it was, so the
      // only thing to do is say so — with `danger`, which `Toast` announces as
      // `role="alert"`: a failure that waits for a pause is a failure missed.
      push({ tone: "danger", message: t("common.state.error") });
      return;
    }
    // Announced with `role="status"`, which is what `Toast` gives an `info`
    // tone: a revocation that worked is not an interruption.
    push({ tone: "success", message: t("auth.sessions.revoked") });
  }, [asking, revoke, push, t]);

  const cancel = useCallback((): void => {
    setAsking(null);
  }, []);

  return (
    <div className={styles.list}>
      {sessions.isSuccess && toolbar !== undefined ? toolbar(sessions.data) : null}

      {sessions.isPending ? <TableSkeleton /> : null}

      {sessions.isError ? (
        <ErrorState
          error={sessions.error}
          onRetry={() => {
            void sessions.refetch();
          }}
        />
      ) : null}

      {sessions.isSuccess && sessions.data.length === 0 ? (
        <EmptyState icon="monitor" title={t("auth.sessions.empty")} body={t("auth.sessions.emptyBody")} />
      ) : null}

      {sessions.isSuccess && sessions.data.length > 0 ? (
        <div className={styles.wrap}>
          <table className={styles.table} aria-label={t("auth.sessions.title")}>
            <thead>
              <tr>
                <th scope="col">{t("auth.sessions.device")}</th>
                <th scope="col">{t("account.sessions.location")}</th>
                <th scope="col">{t("auth.sessions.lastSeen")}</th>
                <th scope="col" className={styles.actionsHead}>
                  <span className={styles.srOnly}>{t("auth.sessions.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((session) => (
                <SessionRow key={session.id} session={session} disabled={pending} onRevoke={setAsking} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {sessions.isSuccess && footer !== undefined ? footer(sessions.data) : null}

      <ConfirmDialog
        open={asking !== null}
        title={t("auth.sessions.revokeConfirmTitle")}
        body={t("auth.sessions.revokeConfirmBody")}
        confirmLabel={t("common.action.confirm")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={() => {
          void confirm();
        }}
        onCancel={cancel}
      />
    </div>
  );
}
